import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { ChannelName } from '@mm/state/model/ChatbotModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useInactiveBotActivator } from '../../editor/common/hooks/use-inactive-bot-activator';

type NonActiveChannels = ChannelName[];

export type IValidateChannels = {
  (param?: ChannelName): NonActiveChannels;
  (param?: ChannelName[]): NonActiveChannels;
};

export const formatChannelName = (channel: ChannelName): string => {
  switch (channel) {
    case 'sms':
      return 'SMS';
    case 'facebook':
      return 'Facebook';
    case 'webChat':
      return 'Webchat';
    case 'instagram':
      return 'Instagram';
    case 'email':
      return 'Email';
    default:
      return '';
  }
};

export const useBotChannels = () => {
  const { openActivateChannelsModal } = useModalActions();
  const { activateBotGuard } = useInactiveBotActivator();
  const chatbotCRUDActions = useChatbotCRUDActions();
  const [busy, setBusy] = useState(false);

  const chatbot = useSelector(activeChatbotSelector);
  const { platformsChannels } = chatbot;

  const toggleChannel = async (
    name: ChannelName,
    active: boolean,
    skipBotActiveGuard?: boolean,
  ) => {
    const botId = chatbot?.id;

    if (!botId && !platformsChannels) {
      return;
    }

    const channels = platformsChannels[name];

    if (!channels?.length) {
      return;
    }

    if (!skipBotActiveGuard) {
      await activateBotGuard();
    }
    await chatbotCRUDActions.togglePlatformChannels({
      botId,
      platformChannels: {
        [name]: channels.map(channel => ({ ...channel, active })),
      },
    });
  };

  const toggleChannels = async (names: ChannelName[], active: boolean) => {
    const botId = chatbot?.id;

    if (!botId && !platformsChannels) {
      return [true];
    }

    const channel = names.every(name => platformsChannels[name]);

    if (!channel) {
      return [true];
    }

    try {
      await activateBotGuard();
      setBusy(true);
      for (let i = 0; i < names.length; i = i + 1) {
        await toggleChannel(names[i], active, true);
      }
      setBusy(false);
      return [false, true];
    } catch (err) {
      setBusy(false);
      return [err, false];
    }
  };

  const isChannelActive = (channel: ChannelName) => {
    const channels = platformsChannels?.[channel];
    if (channel === 'email') {
      return Boolean(
        channels.length && channels.some(channel => channel.active),
      );
    }
    return !!(channels?.length && channels?.every(v => v.active));
  };

  const validateChannels: IValidateChannels = args => {
    if (!args) {
      return [];
    }

    const channels = !Array.isArray(args) ? [args] : args;
    return channels.filter(channel => !isChannelActive(channel));
  };

  const activateChannelGuard = channels =>
    new Promise((resolve: (value?: unknown) => void, reject) => {
      if (!chatbot || !platformsChannels) {
        reject();
        return;
      }

      const nonActiveChannels = validateChannels(channels);

      if (!nonActiveChannels.length) {
        resolve();
        return;
      }

      openActivateChannelsModal({
        channels: nonActiveChannels,
        onSuccess: resolve,
        onReject: reject,
      });
    });

  return {
    busy,
    isChannelActive,
    validateChannels,
    toggleChannel,
    toggleChannels,
    activateChannelGuard,
    formatChannelName,
  };
};
